import api from "../../../lib/axios";
import type { ApiError, BaseResponse } from "../../../lib/api-contract";
import { ApiResultError } from "../../../lib/result-codes";
import type {
  UserCreateRequest,
  UserImportItem,
  UserImportResponse,
  UserImportValidationResponse,
  UserImportValidationError,
  UserItem,
  UserListRequest,
  UserListForAdminResponse,
  UserStatistics,
  UserUpdateRequest,
} from "../types/user.types";

export class UserImportServiceError extends Error {
  readonly importErrors: UserImportValidationError[];

  constructor(message: string, importErrors: UserImportValidationError[] = []) {
    super(message);
    this.name = "UserImportServiceError";
    this.importErrors = importErrors;
  }
}

interface CompatPaging {
  count?: number;
  total?: number;
  Total?: number;
  index?: number;
  size?: number;
}

interface RawUserListResponse {
  paging?: CompatPaging;
  Paging?: CompatPaging;
  list?: UserItem[];
  List?: UserItem[];
}

interface RawUserStatisticsResponse {
  total_users?: number;
  new_users?: number;
  active_users?: number;
  inactive_users?: number;
  engaged_users?: number;
}

const unwrapEnvelope = <T>(envelope: BaseResponse<T> | T): T => {
  if (typeof envelope === "object" && envelope !== null && "data" in envelope) {
    return (envelope as BaseResponse<T>).data as T;
  }
  return envelope as T;
};

const failMessage = (envelope: BaseResponse<unknown>, fallback: string): string =>
  envelope.user_message || envelope.message || fallback;

const importErrorsFrom = (errors: ApiError[] = []): UserImportValidationError[] =>
  errors
    .filter((error) => Boolean(error.sheet || error.row || error.field))
    .map((error) => ({
      code: error.code,
      sheet: error.sheet ?? undefined,
      row: error.row ?? undefined,
      field: error.field ?? undefined,
      message: error.message,
    }));

const importErrorResponse = (error: unknown): BaseResponse<unknown> | undefined => {
  if (error instanceof ApiResultError) return error.response;
  if (typeof error !== "object" || error === null || !("response" in error)) return undefined;
  const response = error.response;
  if (typeof response !== "object" || response === null || !("data" in response)) return undefined;
  return response.data as BaseResponse<unknown>;
};

const normalizeUserList = (payload: RawUserListResponse): UserListForAdminResponse => {
  const paging = payload.paging ?? payload.Paging ?? {};
  return {
    paging: {
      count: paging.total ?? paging.Total ?? paging.count ?? 0,
      index: paging.index ?? 0,
      size: paging.size ?? 0,
    },
    list: payload.list ?? payload.List ?? [],
  };
};

const normalizeUserStatistics = (payload: RawUserStatisticsResponse): UserStatistics => ({
  total_users: payload.total_users ?? 0,
  new_users: payload.new_users ?? 0,
  active_users: payload.active_users ?? 0,
  inactive_users: payload.inactive_users ?? 0,
  engaged_users: payload.engaged_users ?? 0,
});

export async function getUsers(
  request: UserListRequest,
  signal?: AbortSignal,
): Promise<UserListForAdminResponse> {
  const response = await api.get<BaseResponse<RawUserListResponse>>("/v1/portal/user", {
    params: {
      index: request.index,
      size: request.size,
      ...(request.name ? { name: request.name } : {}),
      ...(request.email ? { email: request.email } : {}),
      ...(request.role ? { role: request.role } : {}),
      ...(request.status ? { status: request.status } : {}),
      ...(request.age !== undefined ? { age: request.age } : {}),
      ...(request.date_of_birth ? { date_of_birth: request.date_of_birth } : {}),
      ...(request.join_date_from ? { join_date_from: request.join_date_from } : {}),
      ...(request.join_date_to ? { join_date_to: request.join_date_to } : {}),
      ...(request.sort_key ? { sort_key: request.sort_key } : {}),
      ...(request.sort_direction ? { sort_direction: request.sort_direction } : {}),
    },
    signal,
  });
  return normalizeUserList(unwrapEnvelope(response.data) ?? {});
}

export async function getUserStatistics(signal?: AbortSignal): Promise<UserStatistics> {
  const response = await api.get<BaseResponse<RawUserStatisticsResponse>>(
    "/v1/portal/user/statistic",
    { signal },
  );
  const payload = unwrapEnvelope(response.data);
  if (!payload) throw new Error(failMessage(response.data, "Failed to fetch user statistics"));
  return normalizeUserStatistics(payload);
}

export async function getUserById(id: string, signal?: AbortSignal): Promise<UserItem> {
  const response = await api.get<BaseResponse<UserItem>>(`/v1/portal/user/get/${id}`, { signal });
  const payload = unwrapEnvelope(response.data);
  if (!payload) throw new Error(failMessage(response.data, "Failed to load user"));
  return payload;
}

export async function createUser(request: UserCreateRequest): Promise<UserItem> {
  const response = await api.post<BaseResponse<UserItem>>("/v1/portal/user/create", {
    data: request,
  });
  const payload = unwrapEnvelope(response.data);
  if (!payload) throw new Error(failMessage(response.data, "Failed to create user"));
  return payload;
}

export async function importUsers(users: UserImportItem[]): Promise<UserImportResponse> {
  try {
    const response = await api.post<BaseResponse<UserImportResponse>>(
      "/v1/portal/user/import",
      { users },
    );
    const payload = response.data;
    if (!payload.success || !payload.data) {
      throw new UserImportServiceError(
        failMessage(payload, "Failed to import users"),
        importErrorsFrom(payload.errors),
      );
    }
    return payload.data;
  } catch (error) {
    if (error instanceof UserImportServiceError) throw error;
    const response = importErrorResponse(error);
    throw new UserImportServiceError(
      response ? failMessage(response, "Failed to import users") : "Failed to import users",
      importErrorsFrom(response?.errors),
    );
  }
}

export async function validateImportUsers(users: UserImportItem[]): Promise<UserImportValidationResponse> {
  const response = await api.post<BaseResponse<UserImportValidationResponse>>("/v1/portal/user/import/validate", { users });
  const payload = unwrapEnvelope(response.data);
  if (!payload) throw new Error(failMessage(response.data, "Failed to validate users"));
  return payload;
}

export async function updateUser(id: string, request: UserUpdateRequest): Promise<UserItem> {
  const response = await api.put<BaseResponse<UserItem>>(`/v1/portal/user/update/${id}`, {
    data: request,
  });
  const payload = unwrapEnvelope(response.data);
  if (!payload) throw new Error(failMessage(response.data, "Failed to update user"));
  return payload;
}

export async function deactivateUser(id: string): Promise<void> {
  await api.post(`/v1/portal/user/deactivate/${id}`);
}
